<script setup lang="ts">
import getCsrfToken from '@@/bits/csrf_token'
import currentUser from '@@/bits/current_user'
import { captureException } from '@@/bits/error_tracker'
import { ww } from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { currentUrl, navigateTo } from '@@/bits/location'
import { SnackbarNotificationType } from '@@/enums'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { DiagnosticResults } from '@@/vuecomponents/ContactFormDiagnostic.vue'
import ContactFormDiagnostic from '@@/vuecomponents/ContactFormDiagnostic.vue'
import ContactFormTextInput from '@@/vuecomponents/ContactFormTextInput.vue'
import GlobalSnackbar from '@@/vuecomponents/GlobalSnackbar.vue'
import BaseButton from '@@/vuecomponents/base_button.vue'
import { HTTPMethod, fetchJson } from '@padlet/fetch'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
// Show current response time -- retrieve from Front app
// For future -- enable screenshot/document/etc. attachments

enum FormError {
  Unknown = 'Unknown',
  MaxFilesExceeded = 'MaxFilesExceeded',
  FileTooLarge = 'FileTooLarge',
  FileTypeNotAllowed = 'FileTypeNotAllowed',
}

type ContactFormJsonResponse = { redirect_to?: string; error?: FormError }
type ContactReasonValue = { title: string; value: string; id: string }

const CONTACT_REASONS = {
  account: {
    title: __('Account / Login'),
    value: 'Account / Login',
    id: 'account',
  },
  accessibility: {
    title: __('Accessibility'),
    value: 'Accessibility',
    id: 'accessibility',
  },
  billing: {
    title: __('Billing'),
    value: 'Billing',
    id: 'billing',
  },
  bug: {
    title: __('Bug report'),
    value: 'Bug report',
    id: 'bug',
  },
  feedback: {
    title: __('Feedback'),
    value: 'Feedback',
    id: 'feedback',
  },
  report: {
    title: __('Report a padlet'),
    value: 'Report a padlet',
    id: 'report',
  },
  quotation: {
    title: __('Request a quote'),
    value: 'Request a quote',
    id: 'quotation',
  },
  mapDomain: {
    title: __('Map a domain'),
    value: 'Map a domain',
    id: 'mapDomain',
  },
  others: {
    title: __('Others'),
    value: 'Others',
    id: 'others',
  },
}

const MAX_FILE_SIZE_MB = 18
const MAX_FILES_ALLOWED = 3

const FORM_ERROR_MESSAGE: Record<FormError, string> = {
  [FormError.Unknown]: __("We're sorry, the form could not be submitted. Please try again."),
  [FormError.MaxFilesExceeded]: __('Too many files are selected. Please select up to %{numFilesAllowed} files.', {
    numFilesAllowed: MAX_FILES_ALLOWED,
  }),
  [FormError.FileTooLarge]: __(
    'The file(s) you selected are too large. Total file size must not exceed %{fileSize}MB.',
    { fileSize: MAX_FILE_SIZE_MB },
  ),
  [FormError.FileTypeNotAllowed]: __('File type is not supported. Please select image files only.'),
}

const FORM_RADIO_GROUP_LABEL = computed<string>(() => {
  return __('%{radioGroupLabel} *', { radioGroupLabel: __('What do you need help with?') })
})

const { isBiggerThanTabletLandscape } = storeToRefs(useWindowSizeStore())
const { isDarkMode } = storeToRefs(useDarkModeStore())
const { setSnackbar } = useGlobalSnackbarStore()

const message = ref(
  ww.vueStartingState && ww.vueStartingState.message ? decodeURIComponent(ww.vueStartingState.message) : '',
)
const selected = ref(
  ww.vueStartingState && ww.vueStartingState.selected ? decodeURIComponent(ww.vueStartingState.selected) : '',
)
const showFormGenerator = ref(ww.vueStartingState?.showFormGenerator ?? false)
const showMapDomainForm = ref(
  (ww.vueStartingState?.showMapDomainForm || ww.vueStartingState?.showFormGenerator) ?? false,
)

const navigating = ref(false)
const selectableContactReasons = ref<Record<string, ContactReasonValue>>({})

const attachments = ref<
  {
    filename: string
    mime_type: string
    size: number
    content: string
    encoding?: string
  }[]
>([])

const formInput = ref({
  name: {
    id: 'name',
    name: __('Name'),
    value: currentUser.name ? currentUser.name : '',
    valid: !!currentUser.name,
  },
  email: {
    id: 'email',
    name: __('Email'),
    value: currentUser.email ? currentUser.email : '',
    valid: !!currentUser.email,
  },
  padletUrl: {
    id: 'padletUrl',
    name: __('Padlet Link'),
    value: '',
  },
  domain: {
    id: 'domain',
    name: __('I would like to map this domain'),
    value: '',
    valid: false,
  },
  mapPadletUrl: {
    id: 'mapPadletUrl',
    name: __('Map the domain to this padlet'),
    value: '',
  },
  registrarName: {
    id: 'registrarName',
    name: __('I am using this domain name registrar'),
    value: '',
  },
})

const attachmentsLoadingPromise = ref<Promise<unknown> | null>(null)

const diagnosticResults = ref<DiagnosticResults | null>(null)

const formRef = ref<HTMLFormElement>()
const attachmentsInputRef = ref<HTMLInputElement>()

const formMessageLabel = computed<string>(() => {
  if (selected.value === 'Map a domain') return __('Any other question, comment or issue?')
  return __('%{messageLabel} *', { messageLabel: __('What is your question, comment, or issue?') })
})

const formAttachmentsLabel = computed<string>(() => {
  return __(
    'Please add up to %{numFilesAllowed} additional screenshots to help us understand your issue (maximum total file size is %{fileSize}MB)',
    { numFilesAllowed: MAX_FILES_ALLOWED, fileSize: MAX_FILE_SIZE_MB },
  )
})

const submitButtonText = computed<string>(() => {
  return navigating.value ? __('Submitting...') : __('Submit')
})

const formGeneratedUrl = computed<string>(() => {
  if (!showFormGenerator.value) {
    return ''
  }

  const generatedUrl = new URL(currentUrl({ withQueryString: false }))

  if (selected.value.length > 0) {
    generatedUrl.searchParams.append('selected', selected.value)
    if (selected.value === 'Map a domain') {
      generatedUrl.searchParams.append('map_domain', 'true')
    }
  }

  if (message.value.length > 0) {
    generatedUrl.searchParams.append('message', message.value)
  }

  return generatedUrl.href
})

const mapDomainMessage = computed<string>(() => {
  return `I would like to map ${formInput.value.domain.value} to ${formInput.value.mapPadletUrl.value}. I am using ${formInput.value.registrarName.value}. Other comments: ${message.value}`
})

const shouldAttachDiagnosticResults = computed<boolean>(() => {
  if (!diagnosticResults.value) return false
  const hasDiagnosticFailed = Object.values(diagnosticResults.value).some(
    (diagnosticResult: boolean | undefined) => !diagnosticResult,
  )
  return hasDiagnosticFailed || selected.value === 'Bug report'
})

function onAttachmentsPicked(event: Event): void {
  const target = event.target as HTMLInputElement | null
  if (!target) return
  if (!target.files || target.files.length === 0) return

  const files = Array.from(target.files)
  if (files.length > MAX_FILES_ALLOWED) {
    showErrorMessage(FormError.MaxFilesExceeded)
    clearAttachments()
    return
  }

  const totalFileSize = files.reduce((sum: number, file: File) => sum + file.size, 0)
  if (totalFileSize > MAX_FILE_SIZE_MB * 1024 * 1024) {
    showErrorMessage(FormError.FileTooLarge)
    clearAttachments()
    return
  }

  if (files.some((file) => !file.type.startsWith('image/'))) {
    showErrorMessage(FormError.FileTypeNotAllowed)
    clearAttachments()
    return
  }

  // Clear the attachments array every time new files are picked
  attachments.value = []

  attachmentsLoadingPromise.value = Promise.all(
    files.map((file: File): Promise<void> => {
      return new Promise((resolve) => {
        // File content must be converted to base64 encoded string to send in a
        // JSON request.
        const reader = new FileReader()
        reader.addEventListener('load', (): void => {
          if (typeof reader.result === 'string') {
            const base64Content = reader.result.replace(/^data:[\w/*]+;base64,/, '')
            attachments.value = [
              ...attachments.value,
              {
                filename: file.name,
                mime_type: file.type,
                size: file.size,
                content: base64Content,
                encoding: 'base64',
              },
            ]
          }
          resolve()
        })
        reader.readAsDataURL(file)
      })
    }),
  )
}

async function submit(): Promise<void> {
  // Only submit after attachments are loaded
  attachmentsLoadingPromise.value && (await attachmentsLoadingPromise.value)

  const userData = {
    name: formInput.value.name.value,
    email: formInput.value.email.value,
    message: selected.value === 'Map a domain' ? mapDomainMessage.value : message.value,
    selected: selected.value,
    padlet_url: formInput.value.padletUrl.value,
    attachments: attachments.value,
    diagnostics: processDiagnosticResults(),
  }

  navigating.value = true
  try {
    const options = { method: HTTPMethod.post, csrfToken: getCsrfToken(), jsonData: userData }
    const response: ContactFormJsonResponse = await fetchJson(`/contact-us/mail`, options)

    if (response.error) {
      showErrorMessage(response.error)
    } else if (response.redirect_to) {
      navigateTo(response.redirect_to)
      // block this page from being accessed -- if form not submitted
      clear()
    }
  } catch (error) {
    captureException(error)
    showErrorMessage(FormError.Unknown)
  } finally {
    navigating.value = false
  }
}

function clear(): void {
  if (formRef.value) {
    clearAttachments()
    formRef.value.reset()
  }
}

function clearAttachments(): void {
  if (attachmentsInputRef.value) {
    attachmentsInputRef.value.value = ''
    attachments.value = []
  }
}

function showErrorMessage(error: FormError): void {
  setSnackbar({
    message: FORM_ERROR_MESSAGE[error],
    notificationType: SnackbarNotificationType.error,
  })
}

function update(payload): void {
  const inputId = payload.id
  formInput.value[inputId] = {
    id: payload.id,
    name: payload.name,
    value: payload.value,
    valid: payload.valid,
  }
}

function handleDiagnostics(payload: DiagnosticResults): void {
  diagnosticResults.value = payload
}

function processDiagnosticResults(): DiagnosticResults | null {
  if (!shouldAttachDiagnosticResults.value) return null
  return diagnosticResults.value
}

onMounted(() => {
  selectableContactReasons.value = Object.entries(CONTACT_REASONS)
    .filter((entry) => (showMapDomainForm.value ? true : entry[0] !== 'mapDomain'))
    .reduce((acc, keyValueTuple) => ({ ...acc, [keyValueTuple[0]]: keyValueTuple[1] }), {}) as Record<
    string,
    ContactReasonValue
  >
})
</script>

<template>
  <div class="contact-form-contain">
    <form
      ref="formRef"
      :class="{
        tablet: !isBiggerThanTabletLandscape,
        'dark-mode': isDarkMode,
      }"
      @submit.prevent="submit"
    >
      <div class="data-row">
        <ContactFormTextInput
          :id="formInput.name.id"
          :name="formInput.name.name"
          type="text"
          :value="formInput.name.value"
          :rules="{ required: true }"
          placeholder="Harry Potter"
          @update="update"
        />
      </div>
      <div class="data-row">
        <ContactFormTextInput
          :id="formInput.email.id"
          :name="formInput.email.name"
          type="text"
          :value="formInput.email.value"
          :rules="{ required: true, email: true }"
          placeholder="harrypotter@gryffindor.com"
          @update="update"
        />
      </div>
      <fieldset class="data-row options">
        <legend class="data-label">{{ FORM_RADIO_GROUP_LABEL }}</legend>
        <div class="data-options-container">
          <label v-for="(reason, viz) in selectableContactReasons" :key="viz" :for="reason.id">
            <input
              :id="reason.id"
              v-model="selected"
              class="visually-hidden"
              type="radio"
              name="reason"
              required
              :value="reason.value"
            />
            <span class="radio-label">{{ reason.title }}</span>
          </label>
        </div>
      </fieldset>
      <template v-if="selected === 'Map a domain'">
        <div class="data-row">
          <ContactFormTextInput
            :id="formInput.domain.id"
            :name="formInput.domain.name"
            type="text"
            :value="formInput.domain.value"
            :rules="{ required: true }"
            placeholder="myblog.com"
            @update="update"
          />
        </div>
        <div class="data-row">
          <ContactFormTextInput
            :id="formInput.mapPadletUrl.id"
            :name="formInput.mapPadletUrl.name"
            type="url"
            :value="formInput.mapPadletUrl.value"
            :rules="{ required: true }"
            placeholder="https://padlet.com/gallery/harrypotterquiz"
            @update="update"
          />
        </div>
        <div class="data-row">
          <ContactFormTextInput
            :id="formInput.registrarName.id"
            :name="formInput.registrarName.name"
            type="text"
            :value="formInput.registrarName.value"
            :rules="{ required: true }"
            placeholder="Namecheap, Gandi, or something else"
            @update="update"
          />
        </div>
      </template>
      <div v-if="selected !== 'Map a domain'" class="data-row">
        <ContactFormTextInput
          :id="formInput.padletUrl.id"
          :name="formInput.padletUrl.name"
          type="url"
          :value="formInput.padletUrl.value"
          :rules="{ required: selected === 'Bug report' || selected === 'Report a padlet' }"
          placeholder="https://padlet.com/gallery/harrypotterquiz"
          @update="update"
        />
      </div>
      <div class="data-row">
        <label class="data-label" for="change-user-message">{{ formMessageLabel }}</label>
        <div class="data-value">
          <textarea
            id="change-user-message"
            v-model="message"
            name="message"
            rows="10"
            required
            :placeholder="__('I lost my wand. Please help me!')"
          ></textarea>
        </div>
      </div>
      <div class="data-row">
        <label class="data-label" for="attachments">{{ formAttachmentsLabel }}</label>
        <div class="data-value">
          <input
            id="attachments"
            ref="attachmentsInputRef"
            type="file"
            name="attachments"
            accept="image/*"
            multiple
            @change="onAttachmentsPicked"
          />
        </div>
      </div>
      <div class="data-row submit-button">
        <base-button variation="fat-contained" color-scheme="black" class="submit-button">{{
          submitButtonText
        }}</base-button>
        <div
          v-if="shouldAttachDiagnosticResults"
          class="mx-2 mt-2 flex justify-center items-center space-s-1 text-dark-text-200 dark:text-light-text-200"
        >
          <p class="m-0 text-body-small">
            {{
              __(
                'Your browser and network diagnostics will be included for debugging purposes when you submit this form',
              )
            }}
          </p>
          <OzPlainButton
            href="https://padlet.help/l/en/article/uym730l0xb"
            target="_blank"
            :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
            :size-preset="OzPlainButtonSizePreset.H28px"
            :aria-label="__('Go to diagnostics article')"
          >
            <OzIcon name="help" :size="20" />
          </OzPlainButton>
        </div>
      </div>
    </form>
    <div v-if="showFormGenerator" class="break-all mx-auto my-0 text-center w-6/12">
      <h4>Generated URL</h4>
      <a :href="formGeneratedUrl" class="text-dark-text dark:text-light-text" target="_blank">{{ formGeneratedUrl }}</a>
    </div>

    <ContactFormDiagnostic @result="handleDiagnostics" />
    <GlobalSnackbar />
  </div>
</template>

<style lang="scss" scoped>
.contact-form-contain {
  width: 100%;
}

form {
  max-width: 836px;
  min-width: 630px;
  margin: 0 auto;
  height: fit-content;
  width: 60%;

  .data-row {
    margin-bottom: 46px;

    :deep(.label) {
      font-size: 16px;
      font-weight: bold;
      line-height: 30px;
      margin: 0px 0px 12px 0px;
    }

    .data-label {
      font-size: 16px;
      font-weight: bold;
      line-height: 30px;
      display: inline-block;
      margin: 0px 0px 16px 0px;
      color: var(--body-text-color);
    }

    textarea {
      border: 2px solid var(--input-field-bd-color);
      background: var(--input-field-bg-color);
      color: var(--input-text-color);
      -webkit-box-sizing: border-box;
      -moz-box-sizing: border-box;
      box-sizing: border-box;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      width: 100%;
      margin: 0;
      font-size: 14px;
      padding: 12px 14px;
      border-radius: 16px;
      line-height: 30px;
      transition: all 0.3s;
    }

    textarea:focus {
      border-color: var(--input-field-hl-color);
      box-shadow: 0 0 8px var(--input-field-focused-box-shadow);
      outline: 0 none;
    }

    textarea::placeholder {
      color: var(--input-field-ph-text-color);
    }
  }

  .data-row .submit-button {
    width: 100%;
  }
}

fieldset,
legend {
  padding: 0;
}

fieldset {
  margin: 0;
  border: none;
}

.data-row.options {
  display: flex;
  flex-direction: column;

  .data-options-container {
    display: flex;
    flex-wrap: wrap;

    // visually hide radio input elements
    input.visually-hidden {
      position: absolute;
      clip: rect(0 0 0 0);
      height: 1px;
      width: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
    }

    label {
      background: var(--radio-background-color);
      display: inline-block;
      margin-right: 12px;
      margin-bottom: 12px;
      border-radius: 10px;
      cursor: pointer;
      font-size: 14px;
      transition: 0.3s;

      &:hover {
        background: var(--radio-hover-color);
      }
    }

    span {
      display: block;
      position: relative;
      white-space: nowrap;
      border-radius: 10px;
      padding: 7px 16px 7px 30px;
      transition: all 0.3 linear;
    }

    span:before {
      content: '';
      position: absolute;
      left: 10px;
      top: 50%;
      -webkit-transform: translatey(-50%);
      transform: translatey(-50%);
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: var(--radio-input-background-color);
      border: 1px solid var(--radio-input-border-color);
      transition: all 0.3 linear;
    }

    input[type='radio']:checked + span {
      background: var(--radio-checked-background-color);
    }

    input[type='radio']:checked + span:before {
      background: var(--radio-checked-input-background);
      border: none;
      left: 11px;
    }
  }
}

.error-message {
  position: fixed;
  font-size: 14px;

  :deep(button) {
    border-radius: 8px;
    margin-right: 14px;
    cursor: pointer;
    border: none;
  }
}

form.tablet {
  width: 100%;
  min-width: unset;
  box-sizing: border-box;
  padding: 0px 14px;

  .data-row {
    margin-bottom: 24px;

    input[type='text'],
    textarea {
      margin-top: 8px;
    }
  }

  .data-options-container label {
    margin-right: 10px;
    margin-bottom: 10px;
  }
}
</style>
